import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { IMAGES, WHY_CHOOSE } from "../mock";
import { PageHero, Eyebrow, Icon, CtaBanner, Reveal } from "../components/shared";

const expertise = [
  "Network Infrastructure & Structured Cabling",
  "Wireless & Connectivity Solutions",
  "Video Conferencing & Unified Communications",
  "Audio Visual Solutions",
  "Access Control",
  "Security Systems",
  "Energy-Efficient Air Conditioning & Cooling Solutions",
];

const paragraphs = [
  "At Armstrong Technologies, we specialise in providing high-quality technical installations, maintenance, and support services. Our experienced team works with businesses, public & private sector, property managers, and homeowners to design, install, and maintain systems that improve security, connectivity, efficiency, and performance.",
  "We pride ourselves on delivering professional service, transparent communication, and solutions tailored to each client’s requirements.",
  "Our mission is simple: deliver dependable systems that help our clients operate safely, efficiently, and confidently.",
  "We combine technical expertise with practical experience to ensure every project is completed to the highest standard.",
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        crumb="About"
        eyebrow="About Us"
        title="Your Trusted Technical Partner"
        subtitle="Founded on a commitment to quality, reliability, and customer satisfaction, Armstrong Technologies provides professional technical services and installation solutions across a wide range of industries."
      />

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal>
              <h2 className="font-display font-extrabold text-[#0b2545] text-[34px] lg:text-[44px] hero-heading">
                Technology Solutions. Expertly Delivered.
              </h2>
              <div className="mt-8 space-y-5 text-[16px] text-slate-600 leading-relaxed">
                <p>At Armstrong Technologies, we deliver innovative, reliable and future-focused technology solutions designed to help organisations work smarter, communicate better and operate more efficiently.</p>
                <p>With over 25 years of experience in the IT industry, we have built a reputation for providing high-quality technology and infrastructure solutions to businesses, educational establishments and government organisations.</p>
              </div>
            </Reveal>
            <Reveal delay={120} y={40} className="overflow-hidden rounded-[20px] shadow-xl">
              <img src={IMAGES.partnership} alt="Trusted technology partnership" className="w-full h-[420px] object-cover" />
            </Reveal>
          </div>

          <Reveal className="mt-16">
            <p className="text-[16px] font-semibold text-[#0b2545]">Our extensive expertise covers:</p>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {expertise.map((e, i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl bg-[#eaf2ff] px-4 py-3.5">
                  <Check className="w-4 h-4 text-[#16a34a] mt-0.5 shrink-0" strokeWidth={3} />
                  <span className="text-[14px] text-[#0b2545]">{e}</span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-[16px] text-slate-600 leading-relaxed max-w-3xl">
              From initial consultation and design through to installation, integration and ongoing support, our experienced team delivers solutions tailored to your organisation's specific requirements.
            </p>

            <div className="mt-14 max-w-3xl">
              <h3 className="font-display font-extrabold text-[#0b2545] text-[26px] lg:text-[34px] hero-heading">Quality. Expertise. Customer Care.</h3>
              <div className="mt-5 space-y-5 text-[16px] text-slate-600 leading-relaxed">
                <p>We believe exceptional technology solutions are built on more than just great products — they depend on expert installation, attention to detail and outstanding customer service.</p>
                <p>That's why customer care is at the heart of everything we do. Our commitment to quality workmanship and professional service has earned us the trust of our customers and is reflected in their testimonials.</p>
                <p className="font-semibold text-[#0b2545]">Armstrong Technologies — delivering technology solutions you can rely on.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#eaf2ff]">
        <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <Reveal>
              <h2 className="font-display font-extrabold text-[#0b2545] text-[34px] lg:text-[44px] hero-heading">
                Delivering Reliable Technical Services
              </h2>
              <div className="mt-8 space-y-5">
                {paragraphs.map((p, i) => (
                  <p key={i} className="text-[16px] text-slate-600 leading-relaxed">{p}</p>
                ))}
              </div>
            </Reveal>
            <Reveal delay={120} y={40} className="overflow-hidden rounded-[20px] shadow-xl lg:mt-4">
              <img src={IMAGES.reliable} alt="Reliable technical services" className="w-full h-[500px] object-cover" />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#eaf2ff]">
        <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
          <Reveal>
            <p className="text-[12px] font-semibold tracking-[0.25em] text-[#1e73ff] uppercase mb-5">Why Choose Us</p>
            <h2 className="font-display font-extrabold text-[#0b2545] text-[38px] lg:text-[56px] hero-heading max-w-2xl">
              The Difference Is in the Details
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 gap-x-16 gap-y-2">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={i} delay={(i % 2) * 90} className="flex gap-5 py-8 border-t border-black/10">
                <div className="shrink-0 w-11 h-11 rounded-full bg-[#1e73ff] flex items-center justify-center">
                  <Icon name={w.icon} className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-[18px] text-[#0b2545]">{w.title}</h3>
                  <p className="mt-2 text-[15px] text-slate-500 leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Link to="/services" className="mt-12 inline-flex items-center gap-2 rounded-full bg-[#1e73ff] text-white shadow-sm shadow-[#1e73ff]/25 text-[15px] font-semibold px-7 py-4 hover:bg-[#155ed6] transition-colors">
            Explore our services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
